import { API_CONFIG, API_ENDPOINTS, POLLING } from '../utils/constants';
import type { 
  ApiResponse, 
  User, 
  VideoGenerationRequest, 
  VideoGenerationResponse,
  VideoStatusResponse,
  Video,
  ImageGenerationRequest,
  ImageGenerationResponse,
} from '../types/api';

class ApiClient {
  private baseUrl: string;
  private getAccessToken: (() => Promise<string | undefined>) | null = null;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  setAccessTokenGetter(getter: () => Promise<string | undefined>) {
    this.getAccessToken = getter;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    let authHeader = {};
    if (this.getAccessToken) {
      const token = await this.getAccessToken();
      if (token) {
        authHeader = { Authorization: `Bearer ${token}` };
      }
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        status: 'error' as const,
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new ApiError(errorData.message, response.status);
    }

    return await response.json();
  }

  // User methods
  async getUser(request: { supabase_id: string }): Promise<User> {
    let authHeader = {};
    if (this.getAccessToken) {
      const token = await this.getAccessToken();
      if (token) {
        authHeader = { Authorization: `Bearer ${token}` };
      }
    }

    const response = await fetch(
      `${this.baseUrl}${API_ENDPOINTS.USER.GET}?supabase_user_id=${encodeURIComponent(request.supabase_id)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to get user' }));
      throw new ApiError(errorData.error || 'Failed to get user', response.status);
    }

    // Backend returns user data directly, not wrapped in ApiResponse
    return await response.json();
  }

  // Video methods
  async createVideo(request: VideoGenerationRequest): Promise<VideoGenerationResponse> {
    const response = await this.makeRequest<VideoGenerationResponse>(
      API_ENDPOINTS.GENERATE.CREATE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    if (response.status === 'error') {
      throw new ApiError(response.message || 'Failed to create video', 403, response.data);
    }
    return response.data!;
  }

  async getVideos(): Promise<Video[]> {
    const response = await this.makeRequest<Video[]>(API_ENDPOINTS.VIDEOS.LIST, {
      method: 'GET',
    });
    if (response.status === 'error') {
      throw new ApiError(response.message || 'Failed to get videos', 500);
    }
    return response.data || [];
  }

  async deleteVideo(videoId: string): Promise<void> {
    const response = await this.makeRequest<void>(API_ENDPOINTS.VIDEOS.DELETE, {
      method: 'DELETE',
      body: JSON.stringify({ video_id: videoId }),
    });
    if (response.status === 'error') {
      throw new ApiError(response.message || 'Failed to delete video', 500);
    }
  }

  async favoriteVideo(videoId: string, isFavorite: boolean): Promise<void> {
    const response = await this.makeRequest<void>(API_ENDPOINTS.VIDEOS.FAVORITE, {
      method: 'POST',
      body: JSON.stringify({ video_id: videoId, is_favorite: isFavorite }),
    });
    if (response.status === 'error') {
      throw new ApiError(response.message || 'Failed to update favorite', 500);
    }
  }

  // Image methods
  async createImage(request: ImageGenerationRequest): Promise<ImageGenerationResponse> {
    const response = await this.makeRequest<ImageGenerationResponse>(
      API_ENDPOINTS.GENERATE.CREATE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    if (response.status === 'error') {
      throw new ApiError(response.message || 'Failed to create image', 403, response.data);
    }
    return response.data!;
  }

  // Video status methods
  async getVideoStatus(operation_id: string, asset_id?: string): Promise<VideoStatusResponse> {
    // Build query params - always include operation_id, optionally include asset_id
    let statusUrl = `${this.baseUrl}${API_ENDPOINTS.GENERATE.STATUS}?operation_id=${operation_id}`;
    if (asset_id) {
      statusUrl += `&asset_id=${asset_id}`;
    }
    
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new ApiError(
        errorData.error || errorData.message || 'Failed to get video status',
        response.status
      );
    }

    const data = await response.json();
    
    // Normalize response format - backend might return different field names
    return {
      status: data.status || 'processing',
      url: data.url || data.video_url || data.videoUrl,
      error: data.error,
    };
  }

  async pollVideoStatus(options: {
    operation_id: string;
    asset_id?: string; // Optional asset_id to include in query params
    onProgress?: (status: VideoStatusResponse) => void;
    onComplete?: (status: VideoStatusResponse) => void;
    onError?: (error: string) => void;
    interval?: number;
  }): Promise<VideoStatusResponse> {
    const {
      operation_id,
      asset_id,
      onProgress,
      onComplete,
      onError,
      interval = POLLING.VIDEO_STATUS,
    } = options;

    return new Promise((resolve, reject) => {
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 5; // Allow 5 consecutive errors before showing warning
      
      const poll = async () => {
        try {
          const status = await this.getVideoStatus(operation_id, asset_id);
          
          // Reset error counter on successful poll
          consecutiveErrors = 0;

          if (status.status === 'processing') {
            onProgress?.(status);
            // DO NOT TIMEOUT - continue polling indefinitely
            // The backend will eventually complete, even if it takes longer
            setTimeout(poll, interval);
          } else if (status.status === 'completed') {
            // Only complete if we have a URL - otherwise keep polling
            if (status.url) {
              onComplete?.(status);
              resolve(status);
            } else {
              // Completed but no URL - treat as processing to continue polling
              console.warn('⚠️ [ApiClient] Status is completed but no URL - continuing to poll');
              onProgress?.(status);
              setTimeout(poll, interval);
            }
          } else if (status.status === 'failed') {
            const error = status.error || 'Video generation failed';
            onError?.(error);
            reject(new ApiError(error, 500));
          } else if (status.status === 'deleted') {
            // Handle content policy violations that result in deleted status
            const error =
              status.error ||
              'Video generation was deleted due to policy violation';
            onError?.(error);
            reject(new ApiError(error, 400, status));
          }
        } catch (error) {
          consecutiveErrors++;
          console.error(`❌ [ApiClient] Poll error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error);
          
          // If we have too many consecutive errors, show a warning but don't fail
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.warn(`⚠️ [ApiClient] Multiple consecutive poll errors - network may be unstable, but continuing to poll`);
            // Reset counter to allow recovery
            consecutiveErrors = 0;
          }
          
          // DO NOT FAIL on network errors - continue polling
          // Network issues are temporary, backend will eventually respond
          const errorMessage =
            error instanceof ApiError
              ? error.message
              : 'Unknown error occurred';
          
          // Only call onError for non-network errors (like 404, 500, etc.)
          // For network errors, just continue polling
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            // Client errors (4xx) - might be temporary, continue polling
            setTimeout(poll, interval);
          } else if (error instanceof ApiError && error.status >= 500) {
            // Server errors (5xx) - might be temporary, continue polling
            setTimeout(poll, interval);
          } else {
            // Network errors - definitely continue polling
            setTimeout(poll, interval);
          }
        }
      };

      poll();
    });
  }

  // Upload methods
  async uploadImage(
    imageFile: File,
    supabaseUserId?: string
  ): Promise<{ asset_id: string; image_url: string; user_id: string; supabase_user_id?: string; status: string }> {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    // Send Supabase user ID (works for both anonymous and permanent users)
    if (supabaseUserId) {
      formData.append('supabase_user_id', supabaseUserId);
    }

    const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.UPLOAD.IMAGE}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        status: 'error' as const,
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new ApiError(errorData.message || 'Failed to upload image', response.status);
    }

    return await response.json();
  }

  // Stripe Customer Portal
  async createPortalSession(
    userId: string,
    returnUrl?: string
  ): Promise<{ url: string; portal_session_id: string }> {
    const response = await this.makeRequest<{ url: string; portal_session_id: string }>(
      '/api/stripe/create-portal-session',
      {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          return_url: returnUrl || window.location.origin,
        }),
      }
    );

    if (response.status === 'error') {
      throw new ApiError(response.message || 'Failed to create portal session', 500);
    }

    if (!response.data) {
      throw new ApiError('No data returned from portal session creation', 500);
    }

    return response.data;
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const apiClient = new ApiClient();

